import { Component, OnInit, ViewChild } from '@angular/core';
import { PoBreadcrumb, PoModalComponent, PoNotificationService } from '@po-ui/ng-components';
import { finalize } from 'rxjs';
import { UserService } from './user.service';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.css'],
  standalone: false
})
export class UserComponent implements OnInit {
  @ViewChild('newUserModal') newUserModal!: PoModalComponent;
  @ViewChild('rolesModal') rolesModal!: PoModalComponent;
  @ViewChild('assignRolesModal') assignRolesModal!: PoModalComponent;

  loading = false;
  users: any[] = [];
  filteredUsers: any[] = [];
  roles: any[] = [];
  rolesLoading = false;
  availableRoles: any[] = [];
  availableRolesLoading = false;
  selectedUser: any = null;
  search = '';
  newUser = { email: '', nomeCompleto: '' };
  deletingUserEmail: string | null = null;
  creatingUser = false;

  readonly breadcrumb: PoBreadcrumb = {
    items: [{ label: 'Home', link: '/home' }, { label: 'Cadastro' }, { label: 'Cadastro de Usuários' }]
  };

  constructor(
    private readonly userService: UserService,
    private readonly poNotification: PoNotificationService
  ) {}

  ngOnInit(): void {
    this.refreshData();
  }

  refreshData() {
    this.loadUsers();
  }

  loadUsers() {
    this.loading = true;
    this.userService.getClientAppUsers().subscribe({
      next: (users) => {
        this.users = (users || []).map((u) => ({
          ...u,
          nome: u.nome ?? u.nomeCompleto ?? u.name ?? '',
          email: u.email ?? '',
          ativada: (u.ativada ?? u.activated ?? true) ? 'Sim' : 'Não',
          actions: ''
        }));
        this.applySearch();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.poNotification.error('Não foi possível carregar usuários.');
      }
    });
  }

  private catalogRoleIdFromUserAppRole(r: any): string {
    const value = r?.roleId ?? r?.RoleId ?? r?.role?.id ?? r?.role?.Id ?? r?.Role?.id ?? r?.Role?.Id;
    const roleId = value != null && value !== '' ? String(value).trim() : '';
    if (!roleId || roleId === '00000000-0000-0000-0000-000000000000') {
      return '';
    }
    return roleId;
  }

  loadRolesByUser(userId: number, onLoaded?: () => void) {
    this.rolesLoading = true;
    this.userService.getUserRoles(userId).subscribe({
      next: (roles) => {
        this.roles = (roles || []).map((r) => ({
          ...r,
          id: this.catalogRoleIdFromUserAppRole(r),
          name: r.name ?? r.nome ?? r.roleName ?? r.role?.name ?? 'Sem nome',
          actions: ''
        }));
        this.rolesLoading = false;
        onLoaded?.();
      },
      error: () => {
        this.rolesLoading = false;
        this.roles = [];
        this.poNotification.warning('Não foi possível carregar as permissões do usuário.');
      }
    });
  }

  applySearch() {
    const term = this.search.trim().toLowerCase();
    if (!term) {
      this.filteredUsers = [...this.users];
      return;
    }

    this.filteredUsers = this.users.filter(
      (u) => String(u.nome ?? '').toLowerCase().includes(term) || String(u.email ?? '').toLowerCase().includes(term)
    );
  }

  openNewUserModal() {
    this.newUser = { email: '', nomeCompleto: '' };
    this.newUserModal.open();
  }

  createUser() {
    if (!this.newUser.email || !this.newUser.nomeCompleto) {
      this.poNotification.warning('Informe nome e e-mail para criar o usuário.');
      return;
    }
    if (this.creatingUser) {
      return;
    }
    this.creatingUser = true;
    this.userService
      .createClientAppUser(this.newUser)
      .pipe(
        finalize(() => {
          this.creatingUser = false;
        })
      )
      .subscribe({
        next: () => {
          this.poNotification.success('Usuário criado com sucesso.');
          this.newUserModal.close();
          this.loadUsers();
        },
        error: () => this.poNotification.error('Não foi possível criar o usuário.')
      });
  }

  removeUser(user: any) {
    const email = String(user?.email ?? '').trim();
    if (!email || this.deletingUserEmail !== null) {
      return;
    }
    this.deletingUserEmail = email;
    this.userService
      .removeClientAppUser(email)
      .pipe(
        finalize(() => {
          this.deletingUserEmail = null;
        })
      )
      .subscribe({
        next: () => {
          this.poNotification.success('Usuário removido com sucesso.');
          this.loadUsers();
        },
        error: () => this.poNotification.error('Não foi possível remover o usuário.')
      });
  }

  openRolesModal(user: any) {
    this.selectedUser = user;
    const userId = Number(this.selectedUser?.id ?? this.selectedUser?.Id ?? 0);
    this.roles = [];
    if (!userId) {
      this.poNotification.warning('Não foi possível identificar o usuário selecionado.');
      return;
    }
    this.loadRolesByUser(userId);
    this.rolesModal.open();
  }

  openAssignRolesModal() {
    const userId = Number(this.selectedUser?.id ?? this.selectedUser?.Id ?? 0);
    if (!userId) {
      this.poNotification.warning('Não foi possível identificar o usuário.');
      return;
    }
    this.loadAvailableRolesToAssign();
    this.assignRolesModal.open();
  }

  private loadAvailableRolesToAssign() {
    this.availableRolesLoading = true;
    this.availableRoles = [];
    this.userService.getClientAppRoles().subscribe({
      next: (allRoles) => {
        const mapped = (allRoles || []).map((r) => ({
          ...r,
          id: r.id ?? r.roleId ?? r.Id,
          name: r.name ?? r.nome ?? r.roleName ?? 'Sem nome'
        }));
        const ownedIds = new Set(this.roles.map((r) => String(r.id ?? '').trim()).filter((id) => id.length > 0));
        this.availableRoles = mapped.filter((r) => {
          const roleId = String(r.id ?? '');
          return roleId.length > 0 && !ownedIds.has(roleId);
        });
        this.availableRolesLoading = false;
      },
      error: () => {
        this.availableRolesLoading = false;
        this.poNotification.error('Não foi possível carregar as roles disponíveis.');
      }
    });
  }

  assignRole(role: any) {
    const userId = Number(this.selectedUser?.id ?? this.selectedUser?.Id ?? 0);
    const roleId = String(role.id ?? '');
    if (!userId || !roleId) {
      this.poNotification.warning('Não foi possível identificar usuário/role para associar.');
      return;
    }
    this.userService.assignRoleToUser(userId, roleId).subscribe({
      next: () => {
        this.poNotification.success(`Role "${role.name}" associada ao usuário.`);
        this.loadRolesByUser(userId, () => this.loadAvailableRolesToAssign());
      },
      error: () => this.poNotification.error('Não foi possível associar role ao usuário.')
    });
  }

  removeRole(role: any) {
    const userId = Number(this.selectedUser?.id ?? this.selectedUser?.Id ?? 0);
    const roleId = String(role.id ?? this.catalogRoleIdFromUserAppRole(role) ?? '').trim();
    const emptyGuid = '00000000-0000-0000-0000-000000000000';
    if (!userId || !roleId || roleId === emptyGuid) {
      this.poNotification.warning(
        'Não foi possível identificar a role no catálogo para remover. Recarregue a lista e tente novamente.'
      );
      return;
    }
    this.userService.removeRoleFromUser(userId, roleId).subscribe({
      next: () => {
        this.poNotification.success(`Role "${role.name}" removida do usuário.`);
        this.loadRolesByUser(userId, () => this.loadAvailableRolesToAssign());
      },
      error: () => this.poNotification.error('Não foi possível remover role do usuário.')
    });
  }
}
